import { useEffect, useRef } from 'react'
import { apiFetch, ApiError } from '../lib/api.js'
import { useAuth } from './useAuth.js'
import { fullNameFromUser } from '../lib/userName.js'
import type { LearnerProfile } from '../types/index.js'

/**
 * After an OAuth sign-in (LinkedIn/Google/Microsoft), Supabase fills the user's
 * name into `user_metadata`. If the learner profile has no full name yet, seed
 * it so certificates are personalized without the user filling the form. Never
 * overwrites a name the user already set, and does nothing for OTP-only signups
 * (no name in metadata). Idempotent: runs at most once per signed-in user.
 */
export function useSeedProfileName() {
  const { user, session } = useAuth()
  const seededFor = useRef<string | null>(null)

  useEffect(() => {
    if (!session || !user) return
    if (seededFor.current === user.id) return
    const name = fullNameFromUser(user)
    if (!name) return // OTP-only signup or no provider name → nothing to seed
    seededFor.current = user.id

    void (async () => {
      try {
        let existing: LearnerProfile | null = null
        try {
          const res = await apiFetch<{ data: LearnerProfile }>('/api/public/profile')
          existing = res.data
        } catch (err) {
          if (!(err instanceof ApiError && err.status === 404)) throw err
        }
        // Only seed when there is no name yet — never clobber a user-set name.
        if (existing?.fullName) return
        await apiFetch('/api/public/profile', {
          method: 'PUT',
          body: { fullName: name, headline: existing?.headline ?? null },
        })
      } catch {
        // Non-blocking — the user can still set the name manually in the modal.
        seededFor.current = null
      }
    })()
  }, [user, session])
}
